import { useTranslation } from 'react-i18next'
import { FormikHelpers, useFormik } from 'formik'
import * as Yup from 'yup'
import { Alert, AlertTitle, Button, Grid, Stack, TextField } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { CatalogProduct, Device, Heater, HeaterInput } from '../types'
import { createHeater, updateHeater } from '../services/heater'
import CatalogProductCreateButton from './CatalogProductCreateButton'
import { CatalogProductStatus, CatalogProductType } from '../enums'
import CatalogProductAutocomplete from './CatalogProductAutocomplete'
import { useEffect, useState } from 'react'
import { getCatalogProducts } from '../services/catalogProductService'
import HeatingPowerAutocomplete from './HeatingPowerAutocomplete'
import {
  isRequired,
  mustBeLessThanCharacters,
  mustBeNumber,
  mustBeString
} from '../utils/validation'
import { usePolicy } from '../hooks'
import RentalCompanyAutocomplete from './RentalCompanyAutocomplete'
import HeaterInstallFormInstructions from './HeaterInstallFormInstructions'

export interface HeaterInstallFormProps {
  device?: Device
  heater?: Heater | null
  formValues?: Partial<HeaterInput>
  submitButtonText?: string
  onCancel?: () => void
  onError?: (error: unknown) => void
  beforeSubmit?: (values: HeaterInput, helpers: FormikHelpers<HeaterInput>) => void
  afterSubmit?: (heater: Heater, helpers: FormikHelpers<HeaterInput>) => void
}

export default function HeaterInstallForm({
  device,
  heater,
  formValues,
  submitButtonText,
  onCancel,
  onError,
  beforeSubmit,
  afterSubmit
}: HeaterInstallFormProps) {
  /**
   * The translate function.
   */
  const { t } = useTranslation('heater')

  /**
   * The policy state.
   */
  const { isSuperadmin, isManufacturer } = usePolicy()

  /**
   * The catalog products.
   */
  const [catalogProducts, setCatalogProducts] = useState<CatalogProduct[]>([])

  /**
   * Indicates if the serial number input is displayed.
   */
  const [showSerialNumber, setShowSerialNumber] = useState<boolean>(false)

  /**
   * Indicates if the owner input is displayed.
   */
  const [showOwner, setShowOwner] = useState<boolean>(false)

  /**
   * The initial values.
   */
  const initialValues: HeaterInput = {
    heatingPower: heater?.heatingPower ?? formValues?.heatingPower ?? 0,
    serialNumber: heater?.serialNumber ?? formValues?.serialNumber ?? '',
    customerReference: heater?.customerReference ?? formValues?.customerReference ?? '',
    catalogProduct: heater?.catalogProductId ?? formValues?.catalogProduct ?? undefined,
    company: heater?.companyId ?? formValues?.company ?? undefined,
    device: device?.name ?? formValues?.device ?? undefined
  }

  /**
   * The validation schema.
   */
  const validationSchema = Yup.object({
    heatingPower: Yup.number()
      .typeError(mustBeNumber(t('heatingPower')))
      .required(isRequired(t('heatingPower'))),
    serialNumber: Yup.string()
      .typeError(mustBeString(t('serialNumber')))
      .max(255, mustBeLessThanCharacters(t('serialNumber'), 255))
      .nullable(),
    customerReference: Yup.string()
      .typeError(mustBeString(t('customerReference')))
      .max(255, mustBeLessThanCharacters(t('customerReference'), 255))
  })

  /**
   * The formik state.
   */
  const {
    values,
    touched,
    errors,
    isSubmitting,
    setFieldValue,
    setFieldTouched,
    handleSubmit,
    handleBlur,
    handleChange
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  })

  /**
   * The selected catalog product.
   */
  const selectedCatalogProduct = catalogProducts.find(
    (catalogProduct) => catalogProduct.id === values.catalogProduct
  )

  /**
   * Checks if the field has an error.
   */
  function isError(field: keyof HeaterInput): boolean {
    return Boolean(touched[field] && errors[field])
  }

  /**
   * Get the helper text for a field.
   */
  function getHelperText(field: keyof HeaterInput): string | undefined {
    return touched[field] ? errors[field] : undefined
  }

  function handleAfterCatalogProductCreate(catalogProduct: CatalogProduct) {
    handleCatalogProductChange(catalogProduct)
    setCatalogProducts((prev) => [...prev, catalogProduct])
  }

  function handleCatalogProductChange(catalogProduct: CatalogProduct | null) {
    setFieldValue('catalogProduct', catalogProduct?.id ?? null)
    setFieldTouched('catalogProduct', true, false)

    if (catalogProduct?.customData?.heatingPower) {
      setFieldValue('heatingPower', catalogProduct.customData.heatingPower)
      setFieldTouched('heatingPower', true, false)
    }
  }

  /**
   * Handle the submit.
   */
  async function onSubmit(
    values: HeaterInput,
    { setSubmitting, ...helpers }: FormikHelpers<HeaterInput>
  ) {
    try {
      setSubmitting(true)
      beforeSubmit?.(values, {
        setSubmitting,
        ...helpers
      })

      const updatedOrCreatedHeater = heater
        ? await updateHeater(heater.id, values)
        : await createHeater(values)

      setSubmitting(false)
      afterSubmit?.(updatedOrCreatedHeater, {
        setSubmitting,
        ...helpers
      })
    } catch (error) {
      // TODO: Handle the errors.
      setSubmitting(false)
      onError?.(error)
    }
  }

  async function loadCatalogProducts(controller?: AbortController) {
    try {
      const status = isSuperadmin || isManufacturer ? undefined : CatalogProductStatus.VERIFIED
      setCatalogProducts(
        await getCatalogProducts(
          {
            type: CatalogProductType.HEATER,
            status
          },
          {
            signal: controller?.signal
          }
        )
      )
    } catch (_) {
      // Do nothing.
    }
  }

  useEffect(() => {
    const controller = new AbortController()

    loadCatalogProducts(controller)

    return () => controller.abort()
  }, [])

  return (
    <form onSubmit={handleSubmit}>
      <Grid container spacing={6}>
        <Grid item xs={12}>
          {catalogProducts.length > 0 && (
            <>
              {(values.catalogProduct === undefined || values.catalogProduct === null) && (
                <HeaterInstallFormInstructions />
              )}

              <CatalogProductAutocomplete
                options={catalogProducts}
                label={t('model')}
                value={values.catalogProduct ?? undefined}
                onChange={(_, value) => {
                  if (typeof value !== 'string') {
                    handleCatalogProductChange(value)
                  }
                }}
              />
            </>
          )}

          {(values.catalogProduct === undefined || values.catalogProduct === null) && (
            <Stack direction="row" justifyContent="flex-start" sx={{ mt: 3 }}>
              <CatalogProductCreateButton
                type={CatalogProductType.HEATER}
                label={t('addNewCatalogProduct')}
                afterCreate={handleAfterCatalogProductCreate}
              />
            </Stack>
          )}
        </Grid>

        {values.catalogProduct && (
          <>
            {selectedCatalogProduct?.manufacturer && (
              <Grid item xs={12}>
                <TextField
                  label={t('manufacturer')}
                  value={selectedCatalogProduct.manufacturer}
                  disabled
                  fullWidth
                />
              </Grid>
            )}

            {selectedCatalogProduct?.manufacturerProductCode && (
              <Grid item xs={12}>
                <TextField
                  label={t('manufacturerProductCode')}
                  value={selectedCatalogProduct.manufacturerProductCode}
                  disabled
                  fullWidth
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <HeatingPowerAutocomplete
                name="heatingPower"
                label={t('heatingPower')}
                value={values.heatingPower}
                disabled={true}
                error={isError('heatingPower')}
                helperText={getHelperText('heatingPower')}
                fullWidth
                onBlur={handleBlur}
                onChange={handleChange}
              />
            </Grid>

            {formValues?.customerReference !== null && (
              <Grid item xs={12}>
                <TextField
                  name="customerReference"
                  label={t('customerReference')}
                  value={values.customerReference}
                  disabled={isSubmitting || formValues?.customerReference !== undefined}
                  error={isError('customerReference')}
                  helperText={getHelperText('customerReference')}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              {!showOwner ? (
                <Button variant="outlined" onClick={() => setShowOwner(true)}>
                  {t('asset:addCompany')}
                </Button>
              ) : (
                <RentalCompanyAutocomplete
                  name="company"
                  label={t('owner')}
                  value={values.company ?? null}
                  disabled={isSubmitting}
                  fullWidth
                  onChange={(_, value) => {
                    setFieldValue('company', value ?? null)
                    setFieldTouched('company', true, false)
                  }}
                />
              )}
            </Grid>

            {/*<Grid item xs={12}>
              {!showSerialNumber ? (
                <Button variant="outlined" onClick={() => setShowSerialNumber(true)}>
                  {t('addSerialNumber')}
                </Button>
              ) : (
                <TextField
                  name="serialNumber"
                  label={t('serialNumber')}
                  value={values.serialNumber}
                  disabled={isSubmitting}
                  error={isError('serialNumber')}
                  helperText={getHelperText('serialNumber')}
                  fullWidth
                  onBlur={handleBlur}
                  onChange={handleChange}
                />
              )}
            </Grid>*/}
          </>
        )}

        <Grid item xs={12}>
          <Stack direction="row" justifyContent="flex-end" alignItems="center" spacing={2}>
            <Button variant="text" color="inherit" onClick={onCancel}>
              {t('cancel')}
            </Button>
            <LoadingButton
              type="submit"
              variant="contained"
              loading={isSubmitting}
              disabled={values.heatingPower === 0 || isSubmitting}
            >
              {submitButtonText ?? t('save')}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </form>
  )
}
